import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Slide from '@mui/material/Slide';
import { DialogContent, DialogContentText, useTheme, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, Chip, Grid, ToggleButton, ToggleButtonGroup, TextField, InputAdornment, Pagination, TableSortLabel } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import MainCard from '../../Components/Dashboard/MainCard.js';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const valueFormatter = (value) => `${value}`;

const formatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short'
});

const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
};

function UsageChart({ data, timeFilter, onTimeFilterChange }) {
    const chartSettings = React.useMemo(() => {
        return {
            yAxis: [
                {
                    label: 'Usage Count'
                },
            ],
            series: [{ 
                dataKey: 'number_of_hits', 
                label: 'Usage Count', 
                valueFormatter, 
                color: '#28357e' 
            }],
            height: 400,
            margin: { left: 80, right: 80, top: 40, bottom: 60 },
            sx: {
                [`& .${axisClasses.directionY} .${axisClasses.label}`]: {
                    transform: 'translateX(-10px)',
                },
            },
            legend: {
                direction: 'row',
                position: { vertical: 'top', horizontal: 'right' },
                padding: 0,
            },
        };
    }, []);

    const chartData = React.useMemo(() => {
        if (!data || !data.records) return [];
        
        const records = data.records.map(record => ({
            ...record,
            date: new Date(record.accessed_date),
            number_of_hits: parseInt(record.number_of_hits)
        })).sort((a, b) => a.date - b.date);

        let aggregatedData = [];

        switch (timeFilter) {
            case 'weekly':
                const weeklyData = {};
                records.forEach(record => {
                    const weekStart = getWeekStart(record.date);
                    const weekKey = weekStart.toISOString().split('T')[0];
                    
                    if (!weeklyData[weekKey]) {
                        weeklyData[weekKey] = {
                            date: weekStart,
                            number_of_hits: 0,
                            label: `Week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                        };
                    }
                    weeklyData[weekKey].number_of_hits += record.number_of_hits;
                });
                
                aggregatedData = Object.values(weeklyData).sort((a, b) => a.date - b.date);
                break;

            case 'monthly':
                const monthlyData = {};
                records.forEach(record => {
                    const monthKey = `${record.date.getFullYear()}-${record.date.getMonth()}`;
                    
                    if (!monthlyData[monthKey]) {
                        monthlyData[monthKey] = {
                            date: new Date(record.date.getFullYear(), record.date.getMonth(), 1),
                            number_of_hits: 0,
                            label: record.date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
                        };
                    }
                    monthlyData[monthKey].number_of_hits += record.number_of_hits;
                });
                
                aggregatedData = Object.values(monthlyData).sort((a, b) => a.date - b.date);
                break;

            case 'yearly':
                const yearlyData = {};
                records.forEach(record => {
                    const yearKey = record.date.getFullYear().toString();
                    
                    if (!yearlyData[yearKey]) {
                        yearlyData[yearKey] = {
                            date: new Date(record.date.getFullYear(), 0, 1),
                            number_of_hits: 0,
                            label: yearKey
                        };
                    }
                    yearlyData[yearKey].number_of_hits += record.number_of_hits;
                });
                
                aggregatedData = Object.values(yearlyData).sort((a, b) => a.date - b.date);
                break;

            default:
                aggregatedData = records.map(record => ({
                    date: record.date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                    }),
                    number_of_hits: record.number_of_hits,
                    accessed_date: record.accessed_date,
                    label: record.date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                    })
                }));
        }

        return aggregatedData.map(item => ({
            ...item,
            date: item.label || item.date
        }));
    }, [data, timeFilter]);

    if (!chartData.length) {
        return (
            <Typography variant="h6" sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                No data available for chart
            </Typography>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <ToggleButtonGroup
                    value={timeFilter}
                    exclusive
                    onChange={(event, newFilter) => {
                        if (newFilter !== null) {
                            onTimeFilterChange(newFilter);
                        }
                    }}
                    aria-label="time filter"
                    size="small"
                    sx={{
                        '& .MuiToggleButton-root': {
                            px: 3,
                            py: 1,
                            border: '1px solid',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&.Mui-selected': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: 'primary.dark',
                                }
                            },
                            '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'primary.main',
                            }
                        }
                    }}
                >
                    <ToggleButton value="daily">
                        Daily
                    </ToggleButton>
                    <ToggleButton value="weekly">
                        Weekly
                    </ToggleButton>
                    <ToggleButton value="monthly">
                        Monthly
                    </ToggleButton>
                    <ToggleButton value="yearly">
                        Yearly
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Box sx={{ 
                width: '100%', 
                display: 'flex', 
                justifyContent: 'center',
                px: 2
            }}>
                <Box sx={{ width: '70%', minWidth: '600px' }}>
                    <BarChart
                        dataset={chartData}
                        {...chartSettings}
                        leftAxis={{
                            valueFormatter: (value) => {
                                return formatter.format(value);
                            }
                        }}
                        xAxis={[
                            {
                                scaleType: 'band',
                                dataKey: 'date',
                                valueFormatter: (date) => date,
                                categoryGapRatio: 0.6,
                                barGapRatio: 0.3
                            },
                        ]}
                    />
                </Box>
            </Box>
        </Box>
    );
}

export default function UsageStatsDetails({ detailsInfo, handleClose }) {
    const theme = useTheme();
    const [timeFilter, setTimeFilter] = React.useState('daily');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [page, setPage] = React.useState(1);
    const [sortConfig, setSortConfig] = React.useState({
        key: 'accessed_date',
        direction: 'desc'
    });
    const recordsPerPage = 10;

    const descriptionElementRef = React.useRef(null);
    
    React.useEffect(() => {
        if (detailsInfo.open) {
            const { current: descriptionElement } = descriptionElementRef;
            if (descriptionElement !== null) {
                descriptionElement.focus();
            }
        }
    }, [detailsInfo.open]);

    const handleTimeFilterChange = (newFilter) => {
        setTimeFilter(newFilter);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(1);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
        setPage(1);
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key === columnKey) {
            return sortConfig.direction === 'asc' ?
                <ArrowUpwardIcon fontSize="small" sx={{ color: 'white' }} /> :
                <ArrowDownwardIcon fontSize="small" sx={{ color: 'white' }} />;
        }
        return <UnfoldMoreIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />;
    };

    const sortedAndFilteredRecords = React.useMemo(() => {
        if (!detailsInfo.data || !detailsInfo.data.records) return [];
        
        let records = [...detailsInfo.data.records];

        if (searchTerm.trim()) {
            const searchLower = searchTerm.trim().toLowerCase();
            records = records.filter(record => {
                const hitsString = record.number_of_hits.toString();
                const formattedDate = new Date(record.accessed_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                }).toLowerCase();
                const packageName = record.pack_name.toLowerCase();
                const userName = record.user_name.toLowerCase();

                return hitsString.includes(searchLower) ||
                       formattedDate.includes(searchLower) ||
                       packageName.includes(searchLower) ||
                       userName.includes(searchLower);
            });
        }

        records.sort((a, b) => {
            let aValue, bValue;

            switch (sortConfig.key) {
                case 'accessed_date':
                    aValue = new Date(a.accessed_date);
                    bValue = new Date(b.accessed_date);
                    break;
                case 'number_of_hits':
                    aValue = parseInt(a.number_of_hits);
                    bValue = parseInt(b.number_of_hits);
                    break;
                case 'pack_name':
                    aValue = a.pack_name.toLowerCase();
                    bValue = b.pack_name.toLowerCase();
                    break;
                case 'user_name':
                    aValue = a.user_name.toLowerCase();
                    bValue = b.user_name.toLowerCase();
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return records;
    }, [detailsInfo.data, searchTerm, sortConfig]);

    const paginatedRecords = React.useMemo(() => {
        const startIndex = (page - 1) * recordsPerPage;
        const endIndex = startIndex + recordsPerPage;
        return sortedAndFilteredRecords.slice(startIndex, endIndex);
    }, [sortedAndFilteredRecords, page, recordsPerPage]);

    const totalPages = Math.ceil(sortedAndFilteredRecords.length / recordsPerPage);

    const renderContent = () => {
        if (!detailsInfo.data) {
            return (
                <Typography variant="h6" sx={{ mt: 4, textAlign: 'center', color: 'text.secondary' }}>
                    Loading usage statistics details...
                </Typography>
            );
        }

        if (detailsInfo.data.error) {
            return (
                <Typography variant="h6" sx={{ mt: 4, textAlign: 'center', color: 'error.main' }}>
                    {detailsInfo.data.error}
                </Typography>
            );
        }

        if (detailsInfo.data.records && detailsInfo.data.records.length > 0) {
            const { count, records } = detailsInfo.data;
            const firstRecord = records[0];

            return (
                <Box sx={{ mt: 2 }}>
                    <Box sx={{ mb: 4, p: 3, backgroundColor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="h5" sx={{ mb: 2, color: 'primary.main' }}>
                            Usage Summary
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    User Name
                                </Typography>
                                <Typography variant="h6">
                                    {firstRecord.user_name}
                                </Typography>
                            </Box>
                            <Box>
                                                                <Typography variant="subtitle2" color="text.secondary">
                                    Package Name
                                </Typography>
                                <Typography variant="h6">
                                    {firstRecord.pack_name}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Total Records
                                </Typography>
                                <Chip 
                                    label={count} 
                                    color="primary" 
                                    sx={{ fontSize: '1rem', height: '32px' }}
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ mb: 2, color: 'primary.main' }}>
                            Usage Over Time
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'left' }}>
                            <MainCard elevation={3} sx={{ width: 'fit-content', padding: 2 }}>
                                <UsageChart 
                                    data={detailsInfo.data} 
                                    timeFilter={timeFilter}
                                    onTimeFilterChange={handleTimeFilterChange}
                                />
                            </MainCard>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h5" sx={{ color: 'primary.main' }}>
                            Usage Details
                        </Typography>
                        
                        <TextField
                            size="small"
                            placeholder="Search by date, usage count, package, or user..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            sx={{ 
                                width: '300px',
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                        borderColor: 'primary.main',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'primary.main',
                                    },
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {searchTerm ? (
                                `Showing ${paginatedRecords.length} of ${sortedAndFilteredRecords.length} filtered records (${records.length} total)`
                            ) : (
                                `Showing ${paginatedRecords.length} of ${sortedAndFilteredRecords.length} records`
                            )}
                        </Typography>
                        
                        {totalPages > 1 && (
                            <Typography variant="body2" color="text.secondary">
                                Page {page} of {totalPages}
                            </Typography>
                        )}
                    </Box>

                    <TableContainer 
                        component={Paper} 
                        sx={{ 
                            boxShadow: 2, 
                            mb: 2,
                            maxHeight: '500px',
                            overflow: 'auto'
                        }}
                    >
                        <Table 
                            sx={{ minWidth: 650 }} 
                            aria-label="usage stats table"
                            stickyHeader
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ 
                                        backgroundColor: 'primary.main',
                                        color: 'white', 
                                        fontWeight: 'bold',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1,
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                        }
                                    }}
                                        onClick={() => handleSort('accessed_date')}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <span>Accessed Date</span>
                                            {getSortIcon('accessed_date')}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ 
                                        backgroundColor: 'primary.main',
                                        color: 'white', 
                                        fontWeight: 'bold',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1,
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                        }
                                    }}
                                        onClick={() => handleSort('number_of_hits')}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <span>Usage Count</span>
                                            {getSortIcon('number_of_hits')}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ 
                                        backgroundColor: 'primary.main',
                                        color: 'white', 
                                        fontWeight: 'bold',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1,
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                        }
                                    }}
                                        onClick={() => handleSort('pack_name')}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <span>Package Name</span>
                                            {getSortIcon('pack_name')}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ 
                                        backgroundColor: 'primary.main',
                                        color: 'white', 
                                        fontWeight: 'bold',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1,
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                        }
                                    }}
                                        onClick={() => handleSort('user_name')}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <span>User Name</span>
                                            {getSortIcon('user_name')}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedRecords.length > 0 ? (
                                    paginatedRecords.map((record, index) => (
                                        <TableRow 
                                            key={index}
                                            sx={{ 
                                                '&:nth-of-type(odd)': { 
                                                    backgroundColor: 'action.hover' 
                                                },
                                                '&:hover': {
                                                    backgroundColor: 'primary.light',
                                                    '& td': {
                                                        color: 'primary.main'
                                                    }
                                                }
                                            }}
                                        >
                                            <TableCell component="th" scope="row">
                                                {new Date(record.accessed_date).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })}
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={record.number_of_hits} 
                                                    size="small" 
                                                    color="secondary"
                                                />
                                            </TableCell>
                                            <TableCell>{record.pack_name}</TableCell>
                                            <TableCell>{record.user_name}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                {searchTerm ? 'No records found matching your search.' : 'No records available.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 4 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        '&:hover': {
                                            backgroundColor: 'primary.light',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'primary.dark',
                                            }
                                        }
                                    }
                                }}
                            />
                        </Box>
                    )}

                </Box>
            );
        }

        return (
            <Typography variant="h6" sx={{ mt: 4, textAlign: 'center', color: 'text.secondary' }}>
                No usage statistics available
            </Typography>
        );
    };

    return (
        <React.Fragment>
            <Dialog
                fullScreen
                open={detailsInfo.open}
                onClose={handleClose}
                TransitionComponent={Transition}
                className='usage-stats-details'
                sx={{backgroundColor: '#fff !important'}}
            >
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"                        
                        onClick={handleClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1, color: theme.palette.primary.main, lineHeight: 2.5}} variant="h1" component="div">
                        Usage Stats Details
                    </Typography>
                </Toolbar>
                <DialogContent dividers={'paper'} style={{padding: '0px 59px', borderTop: 'none'}}>
                    <DialogContentText
                        id="scroll-dialog-description"
                        ref={descriptionElementRef}
                        tabIndex={-1}
                    >
                        {renderContent()}
                    </DialogContentText>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}